import { useParams, Link, Navigate } from "@/lib/router-compat";
import { blogImages } from "@/assets/blog";
import { useState, useEffect, useMemo } from "react";
import { ArrowRight, ArrowLeft, Share2, Facebook, Linkedin } from "lucide-react";
import Layout from "@/components/Layout";
import PageSEO from "@/components/PageSEO";
import FinalContactSection from "@/components/FinalContactSection";
import RailContactCard from "@/components/RailContactCard";
import { getBlogPost, getRelatedPosts } from "@/data/blog-posts";
import { COMPANY, breadcrumbSchema, faqSchema } from "@/lib/seo";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;
  const [readProgress, setReadProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const article = document.getElementById("blog-article");
      if (!article) return;
      const rect = article.getBoundingClientRect();
      const total = article.scrollHeight;
      const visible = Math.min(Math.max(-rect.top, 0), total);
      setReadProgress(Math.min((visible / total) * 100, 100));
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!post) return <Navigate to="/blog" replace />;

  const relatedPosts = getRelatedPosts(post.slug, post.category);

  // Build TOC from h2 sections
  const toc = post.content
    .filter(s => s.type === "h2")
    .map(s => ({ id: (s.content ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-"), label: s.content }));

  const schemas = [
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Blog", url: "/blog" },
      { name: post.title, url: `/blog/${post.slug}` },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title.length > 110 ? post.title.slice(0, 110) : post.title,
      author: { "@type": "Organization", name: COMPANY.name, url: COMPANY.url },
      publisher: {
        "@type": "Organization",
        name: COMPANY.name,
        logo: { "@type": "ImageObject", url: `${COMPANY.url}/images/hadley-logo.png`, width: 1428, height: 883 },
      },
      datePublished: post.publishedDate,
      dateModified: post.modifiedDate,
      image: `${COMPANY.url}/images/og-image.jpg`,
      description: post.metaDescription,
      mainEntityOfPage: { "@type": "WebPage", "@id": `${COMPANY.url}/blog/${post.slug}` },
      wordCount: post.content.reduce((acc, s) => acc + (s.content?.split(/\s+/).length || 0) + (s.items?.join(" ").split(/\s+/).length || 0), 0),
      inLanguage: "en-US",
    },
    faqSchema(post.faqs),
  ];

  const shareUrl = `${COMPANY.url}/blog/${post.slug}`;

  return (
    <Layout>
      <PageSEO
        title={post.metaTitle}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        schemas={schemas}
      />

      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent">
        <div className="h-full bg-primary transition-all duration-100 ease-out" style={{ width: `${readProgress}%` }} />
      </div>

      <header className="bg-charcoal text-charcoal-foreground">
        <div className="container py-10 md:py-14">
          <nav className="font-body text-xs text-charcoal-foreground/70" aria-label="Breadcrumb"><Link to="/" className="hover:text-primary-light">Home</Link> / <Link to="/blog" className="hover:text-primary-light">Blog</Link> / <span>{post.title}</span></nav>
          <p className="mt-6 font-condensed text-[13px] font-bold uppercase text-primary-light">{post.category}</p>
          <h1 className="mt-2 max-w-[24ch] text-balance font-heading text-[34px] font-extrabold leading-[1.08] text-charcoal-foreground md:text-[40px]">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-3 font-body text-[14px] text-charcoal-foreground/70">
            <span>By <strong className="text-charcoal-foreground">Hadley Energy Solutions Team</strong></span><span>·</span>
            <time dateTime={post.publishedDate}>{new Date(post.publishedDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</time><span>·</span><span>{post.readTime}</span>
          </div>
        </div>
      </header>

      <article id="blog-article" className="section-padding">
        <div className="container max-w-6xl">
          <div className="lg:grid lg:grid-cols-[minmax(0,720px)_280px] lg:justify-between lg:gap-12">
            {/* Main content */}
            <div className="min-w-0">
              {/* Hero image */}
              <div className="mb-10 aspect-video overflow-hidden rounded-[4px] bg-muted">
                <img
                  src={blogImages[post.slug]}
                  alt={post.heroImageAlt}
                  className="w-full h-full object-cover"
                  loading="eager"
                  decoding="async"
                  width="1200"
                  height="675"
                />
              </div>

              {/* Body content */}
              <div className="max-w-[720px] font-body text-[18px] leading-[1.75]">
                {post.content.map((section, i) => {
                  const key = `${section.type}-${i}`;
                  switch (section.type) {
                    case "h2": {
                      const id = (section.content ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
                       return <h2 key={key} id={id} className="mb-4 mt-14 scroll-mt-28 font-heading text-[28px] font-bold leading-tight text-foreground">{section.content}</h2>;
                    }
                    case "h3":
                      return <h3 key={key} className="mb-3 mt-8 font-heading text-[22px] font-bold text-foreground">{section.content}</h3>;
                    case "p":
                      return <p key={key} className="text-muted-foreground leading-relaxed mb-4 [&_a]:text-primary [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-primary/80" dangerouslySetInnerHTML={{ __html: (section.content ?? "").replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>') }} />;
                    case "ul":
                      return (
                        <ul key={key} className="space-y-3 mb-6 pl-0">
                          {section.items?.map((item, j) => (
                            <li key={j} className="flex gap-3 text-muted-foreground leading-relaxed">
                              <span className="mt-3 h-1 w-1 shrink-0 bg-primary" />
                              <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>') }} />
                            </li>
                          ))}
                        </ul>
                      );
                    case "ol":
                      return (
                        <ol key={key} className="space-y-4 mb-6 pl-0 counter-reset-item">
                          {section.items?.map((item, j) => (
                            <li key={j} className="flex gap-3 text-muted-foreground leading-relaxed">
                              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-heading font-bold flex items-center justify-center shrink-0 mt-0.5">{j + 1}</span>
                              <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>') }} />
                            </li>
                          ))}
                        </ol>
                      );
                    case "blockquote":
                      return (
                        <blockquote key={key} className="my-8 border-l-[3px] border-primary px-6 py-2 font-body text-foreground">
                          {section.content}
                        </blockquote>
                      );
                    case "table":
                      return (
                        <div key={key} className="my-8 overflow-x-auto rounded-[4px] border border-border">
                          <table className="w-full border-collapse text-center">
                            <thead className="bg-charcoal text-charcoal-foreground">
                              <tr>
                                {section.headers?.map((h, j) => (
                                  <th key={j} className="px-4 py-3 text-center font-heading font-bold text-sm">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {section.rows?.map((row, r) => (
                                 <tr key={r} className="bg-background">
                                  {row.map((cell, c) => (
                                    <td key={c} className={`px-4 py-3 text-center align-middle border-t border-border text-sm ${c === 0 ? "font-heading font-semibold text-foreground" : "text-muted-foreground"}`}>
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );

                    case "cta":
                      return (
                        <div key={key} className="my-10 rounded-[4px] border-l-[3px] border-primary bg-charcoal p-6 text-charcoal-foreground sm:p-8">
                          <p className="font-heading font-bold text-lg mb-4">{(section.content ?? "").split("—")[0]}</p>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <a href={`tel:${COMPANY.phoneTel}`} className="btn-primary px-5 py-3 text-sm">
                              Call {COMPANY.phone}
                            </a>
                            <Link to="/request-estimate" className="btn-outline-light px-5 py-3 text-sm border-charcoal-foreground/20 text-charcoal-foreground">
                              Request Estimate <ArrowRight className="w-4 h-4" />
                            </Link>
                          </div>
                        </div>
                      );
                    default:
                      return null;
                  }
                })}
              </div>

              {/* Internal Links */}
              {post.internalLinks.length > 0 && (
                 <div className="mt-10 border-y border-border py-6">
                  <h3 className="font-heading font-bold text-lg mb-3">Related Services</h3>
                  <div className="flex flex-wrap gap-2">
                    {post.internalLinks.map(link => (
                      <Link key={link.href} to={link.href} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-background border border-border text-sm font-body hover:text-primary hover:border-primary/30 transition-colors">
                        {link.label} <ArrowRight className="w-3 h-3" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Section */}
              <div className="mt-12">
                 <h2 id="frequently-asked-questions" className="font-heading font-bold text-2xl sm:text-3xl mb-6 text-foreground">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {post.faqs.map((faq, i) => (
                     <div key={i} className="border-t border-border py-5 last:border-b">
                       <h3 className="font-heading text-[18px] font-bold text-foreground">{faq.question}</h3>
                      <div
                         className="mt-2 font-body leading-relaxed text-muted-foreground [&_a]:font-semibold [&_a]:text-primary [&_a]:underline"
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                     </div>
                  ))}
                </div>
              </div>

              {/* Author Bio */}
              <div className="mt-12 flex flex-col items-start gap-4 border-y border-border py-6 sm:flex-row">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-2xl font-heading font-bold text-primary">H</span>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg mb-1">Hadley Energy Solutions Team</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">
                    With 100+ years of combined experience, our team of licensed electricians, HVAC technicians, and certified energy auditors serves Louisville, KY and Southern Indiana. Licensed CE64623 | ME64500 | HM06538.
                  </p>
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 flex items-center gap-3">
                <span className="text-sm font-heading font-semibold text-muted-foreground flex items-center gap-1.5">
                  <Share2 className="w-4 h-4" /> Share:
                </span>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background transition-colors hover:border-primary hover:text-primary" aria-label="Share on Facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background transition-colors hover:border-primary hover:text-primary" aria-label="Share on LinkedIn">
                  <Linkedin className="w-4 h-4" />
                </a>
                <button onClick={() => navigator.clipboard?.writeText(shareUrl)} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background transition-colors hover:border-primary hover:text-primary" aria-label="Copy link">
                  <Share2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Sidebar - TOC + CTA */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-6">
                 <div className="border-t border-border pt-5">
                   <h4 className="font-condensed text-[13px] font-bold uppercase text-primary">On this page</h4>
                  <nav className="space-y-1.5">
                    {toc.map(item => (
                      <a key={item.id} href={`#${item.id}`} className="block text-sm font-body text-muted-foreground hover:text-primary transition-colors leading-snug py-1">
                        {item.label}
                      </a>
                    ))}
                  </nav>
                </div>

                 <RailContactCard dataCta="blog-post-rail" />

                 <div className="border-t border-border pt-5"><h4 className="font-condensed text-[13px] font-bold uppercase text-primary">Related Articles</h4><nav className="mt-3 space-y-2">{relatedPosts.map(item => <Link key={item.slug} to={`/blog/${item.slug}`} className="block font-body text-sm text-muted-foreground hover:text-primary">{item.title}</Link>)}</nav></div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="section-padding border-t border-border bg-background">
          <div className="container">
            <h2 id="related-articles" className="mb-10 font-heading text-2xl font-bold sm:text-3xl">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map(rp => (
                  <Link key={rp.slug} to={`/blog/${rp.slug}`} className="group block overflow-hidden rounded-[4px] border border-border bg-card transition-transform hover:-translate-y-0.5">
                    <div className="aspect-video overflow-hidden bg-muted">
                      <img src={blogImages[rp.slug]} alt={rp.heroImageAlt} className="h-full w-full object-cover" loading="lazy" decoding="async" width="800" height="450" />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-heading font-semibold mb-2">{rp.category}</span>
                      <h3 className="font-heading font-bold text-base leading-snug group-hover:text-primary transition-colors line-clamp-2">{rp.title}</h3>
                       <span className="mt-3 block text-xs text-muted-foreground">{rp.readTime}</span>
                    </div>
                  </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog" className="inline-flex items-center gap-2 text-primary font-heading font-semibold hover:gap-3 transition-all">
                <ArrowLeft className="w-4 h-4" /> Back to All Articles
              </Link>
            </div>
          </div>
        </section>
      )}

      <FinalContactSection source="blog_post_form" />
    </Layout>
  );
};

export default BlogPost;
